import { useCallback, useEffect, useRef, useState } from 'react';

import type { SkillDetailResponse, SkillListItem } from '../lib/api-client';
import { useApiClient } from '../providers/auth-provider';

type Status = 'idle' | 'loading' | 'success' | 'error';

/**
 * ViewModel for the admin Skills view. Loads the full list of skills the
 * tenant has on disk and (on demand) the detail for a selected skill.
 *
 * Gating happens at the route level — non-creator_mode users will get a 403
 * from the backend; the hook surfaces that through `status === 'error'`.
 */
export function useSkills() {
  const apiClient = useApiClient();
  const [skills, setSkills] = useState<SkillListItem[]>([]);
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string | undefined>();
  const [selectedId, setSelectedIdState] = useState<string | null>(null);
  const [detail, setDetail] = useState<SkillDetailResponse | null>(null);
  const [detailStatus, setDetailStatus] = useState<Status>('idle');
  const [detailError, setDetailError] = useState<string | undefined>();

  const load = useCallback(() => {
    setStatus('loading');
    apiClient
      .listSkills()
      .then((data) => {
        setSkills(data);
        setStatus('success');
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Failed to load skills');
        setStatus('error');
      });
  }, [apiClient]);

  const loadRef = useRef(load);
  loadRef.current = load;
  useEffect(() => {
    loadRef.current();
  }, []);

  const setSelectedId = useCallback(
    (id: string | null) => {
      setSelectedIdState(id);
      if (!id) {
        setDetail(null);
        setDetailStatus('idle');
        return;
      }
      setDetailStatus('loading');
      apiClient
        .getSkill(id)
        .then((d) => {
          setDetail(d);
          setDetailStatus('success');
        })
        .catch((err: unknown) => {
          setDetailError(err instanceof Error ? err.message : 'Failed to load skill detail');
          setDetailStatus('error');
        });
    },
    [apiClient],
  );

  return {
    skills,
    status,
    error,
    selectedId,
    setSelectedId,
    detail,
    detailStatus,
    detailError,
    reload: load,
  };
}
